import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import { AuthService } from 'api/services/authService';
import { useAuthStore } from 'store/authStore';

interface Props {
  show: boolean;
  remainingSeconds: number;
  onExtend: () => void;
}

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export default function SessionWarningModal({ show, remainingSeconds, onExtend }: Props) {
  const [loading, setLoading] = useState(false);
  const { clearAuth, setAuth, token, user } = useAuthStore();
  const navigate = useNavigate();

  const handleExtend = async () => {
    setLoading(true);
    try {
      const data = await AuthService.refresh();
      if (token && user) {
        setAuth(data.token, user);
      }
      onExtend();
    } catch {
      // Si el refresh falla, cerrar sesión
      handleCancel();
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    try { await AuthService.logout(); } catch { /* ignore */ }
    clearAuth();
    navigate('/login', { replace: true });
  };

  return (
    <Modal show={show} backdrop="static" keyboard={false} centered>
      <Modal.Header>
        <Modal.Title>
          <i className="ph ph-warning text-warning me-2" />
          Sesión próxima a expirar
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="mb-1">
          Tu sesión expirará en{' '}
          <strong className="text-danger fs-5">{formatTime(remainingSeconds)}</strong>
        </p>
        <p className="text-muted mb-0">
          Si deseas continuar, haz clic en <strong>Continuar sesión</strong>.
          De lo contrario, tu sesión se cerrará automáticamente.
        </p>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={handleCancel} disabled={loading}>
          Cerrar sesión
        </Button>
        <Button variant="primary" onClick={handleExtend} disabled={loading}>
          {loading ? 'Renovando...' : 'Continuar sesión'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
